<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Description</vx-label>
                    <vx-input
                        v-model="form.description"
                        :rows="6"
                        size="lg"
                        data-test="description-input"
                        :error="displayFirstError(validate.description.$errors)"
                    ></vx-input>
                    <vx-char-counter :content="form.description" :min="40" :max="4096" />
                </div>

                <div>
                    <vx-label size="md">Call to action / message flow</vx-label>
                    <p class="text-sm text-slate-500 mb-2">
                        Describe exactly how consent is captured and on which page
                    </p>
                    <vx-input
                        v-model="form.message_flow"
                        :rows="8"
                        size="lg"
                        data-test="message-flow-input"
                        :error="displayFirstError(validate.message_flow.$errors)"
                    ></vx-input>
                    <vx-char-counter :content="form.message_flow" :min="40" :max="4096" />
                </div>

                <div class="flex flex-col gap-3">
                    <vx-label size="md">Sample messages</vx-label>
                    <div v-for="(sample, index) in form.samples" :key="index" :data-test="`sample-${index}`">
                        <div class="flex items-center justify-between mb-1">
                            <span class="text-sm font-bold">Sample {{ index + 1 }}</span>
                            <vx-button
                                v-if="index >= props.minSamples"
                                @click="removeSample(index)"
                                type="button"
                                size="xs"
                                color="muted"
                                :data-test="`remove-sample-${index}`"
                            >
                                Remove <font-awesome-icon widthAuto :icon="faTrash" />
                            </vx-button>
                        </div>
                        <vx-input
                            v-model="form.samples[index]"
                            :rows="3"
                            size="lg"
                            :data-test="`sample-input-${index}`"
                            :error="displayFirstError(validate.samples[index]?.$errors)"
                        ></vx-input>
                        <vx-char-counter :content="sample" :min="20" :max="1024" />
                    </div>
                    <p v-if="props.editing" class="text-sm text-slate-500" data-test="minimum-length-note">
                        Samples two to five and the opt-in message cannot be cleared once set; removing one here leaves
                        it as it was.
                    </p>
                    <vx-button
                        v-if="form.samples.length < MAX_SAMPLES"
                        @click="addSample()"
                        type="button"
                        size="sm"
                        color="muted"
                        class="self-start"
                        data-test="add-sample"
                    >
                        Add sample <font-awesome-icon widthAuto :icon="faPlus" />
                    </vx-button>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <vx-label size="md">Opt-in keywords</vx-label>
                        <vx-input
                            v-model="form.optin_keywords"
                            size="lg"
                            data-test="optin-keywords-input"
                            :error="displayFirstError(validate.optin_keywords.$errors)"
                        ></vx-input>
                    </div>
                    <div>
                        <vx-label size="md">Opt-in message</vx-label>
                        <vx-input
                            v-model="form.optin_message"
                            :rows="3"
                            size="lg"
                            data-test="optin-message-input"
                            :error="displayFirstError(validate.optin_message.$errors)"
                        ></vx-input>
                        <vx-char-counter :content="form.optin_message" :min="20" :max="1024" />
                    </div>
                    <div>
                        <vx-label size="md">Opt-out keywords</vx-label>
                        <vx-input
                            v-model="form.optout_keywords"
                            size="lg"
                            data-test="optout-keywords-input"
                            :error="displayFirstError(validate.optout_keywords.$errors)"
                        ></vx-input>
                    </div>
                    <div>
                        <vx-label size="md">Opt-out message</vx-label>
                        <vx-input
                            v-model="form.optout_message"
                            :rows="3"
                            size="lg"
                            data-test="optout-message-input"
                            :error="displayFirstError(validate.optout_message.$errors)"
                        ></vx-input>
                        <vx-char-counter :content="form.optout_message" :min="20" :max="1024" />
                    </div>
                    <div>
                        <vx-label size="md">Help keywords</vx-label>
                        <vx-input
                            v-model="form.help_keywords"
                            size="lg"
                            data-test="help-keywords-input"
                            :error="displayFirstError(validate.help_keywords.$errors)"
                        ></vx-input>
                    </div>
                    <div>
                        <vx-label size="md">Help message</vx-label>
                        <vx-input
                            v-model="form.help_message"
                            :rows="3"
                            size="lg"
                            data-test="help-message-input"
                            :error="displayFirstError(validate.help_message.$errors)"
                        ></vx-input>
                        <vx-char-counter :content="form.help_message" :min="20" :max="1024" />
                        <p class="text-sm text-slate-500 mt-1">Must include a phone number or an email address</p>
                    </div>
                </div>

                <div>
                    <vx-label size="md">Privacy policy link</vx-label>
                    <vx-input
                        v-model="form.privacy_policy_link"
                        type="url"
                        size="lg"
                        data-test="privacy-policy-link-input"
                        :error="displayFirstError(validate.privacy_policy_link.$errors)"
                    ></vx-input>
                    <p class="text-sm text-slate-500 mt-1">Carriers expect one on a marketing campaign</p>
                </div>

                <div>
                    <vx-label size="md">Terms and conditions link</vx-label>
                    <vx-input
                        v-model="form.terms_and_conditions_link"
                        size="lg"
                        data-test="terms-and-conditions-link-input"
                        :error="displayFirstError(validate.terms_and_conditions_link.$errors)"
                    ></vx-input>
                    <p class="text-sm text-slate-500 mt-1">
                        Without a standalone terms page, link the privacy policy's SMS terms section as the terms
                    </p>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Embedded link</vx-label>
                        <div class="text-sm text-slate-500">Messages carry a link</div>
                    </div>
                    <vx-toggle v-model="form.embedded_link" size="lg" data-test="embedded-link-toggle"></vx-toggle>
                </div>

                <div v-if="form.embedded_link">
                    <vx-label size="md">Embedded link sample</vx-label>
                    <vx-input
                        v-model="form.embedded_link_sample"
                        type="url"
                        size="lg"
                        data-test="embedded-link-sample-input"
                        :error="displayFirstError(validate.embedded_link_sample.$errors)"
                    ></vx-input>
                    <p class="text-sm text-slate-500 mt-1">Optional; TCR does not require a sample</p>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Embedded phone</vx-label>
                        <div class="text-sm text-slate-500">Messages carry a phone number</div>
                    </div>
                    <vx-toggle v-model="form.embedded_phone" size="lg" data-test="embedded-phone-toggle"></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Number pool</vx-label>
                        <div class="text-sm text-slate-500">Only above 49 numbers; Bandwidth still needs a ticket</div>
                    </div>
                    <vx-toggle v-model="form.number_pool" size="lg" data-test="number-pool-toggle"></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Age gated</vx-label>
                        <div class="text-sm text-slate-500">Content restricted to adults</div>
                    </div>
                    <vx-toggle v-model="form.age_gated" size="lg" data-test="age-gated-toggle"></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Direct lending</vx-label>
                        <div class="text-sm text-slate-500">Direct lending or loan arrangement</div>
                    </div>
                    <vx-toggle v-model="form.direct_lending" size="lg" data-test="direct-lending-toggle"></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Auto-renewal</vx-label>
                        <div class="text-sm text-slate-500">Renews at TCR instead of expiring</div>
                    </div>
                    <vx-toggle v-model="form.auto_renewal" size="lg" data-test="auto-renewal-toggle"></vx-toggle>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxCharCounter, VxExpandableCard, VxInput, VxLabel, VxToggle } from '@voxie/frontend-components';
import useVuelidate, { type ServerErrors } from '@vuelidate/core';
import { maxLength, minLength, required, url } from '@vuelidate/validators';
import { computed, toRef } from 'vue';
import type { TcrCampaignPacket } from '~/services/adminTenDlcRegistrationClient.types';
import { displayFirstError } from '~/utils/validation';

// TcrCampaignPayload::MAX_SAMPLES.
const MAX_SAMPLES = 5;

const props = defineProps<{ externalResults: ServerErrors; minSamples: number; editing?: boolean }>();
const form = defineModel<TcrCampaignPacket>({ required: true });

const addSample = () => {
    form.value.samples.push('');
};

const removeSample = (index: number) => {
    form.value.samples.splice(index, 1);
};

const rules = computed(() => ({
    description: { required, minLength: minLength(40), maxLength: maxLength(4096) },
    message_flow: { required, minLength: minLength(40), maxLength: maxLength(4096) },
    samples: Object.fromEntries(
        form.value.samples.map((_, index) => [
            index,
            { required, minLength: minLength(20), maxLength: maxLength(1024) },
        ])
    ),
    optin_keywords: { maxLength: maxLength(255) },
    optin_message: { minLength: minLength(20), maxLength: maxLength(1024) },
    optout_keywords: { maxLength: maxLength(255) },
    optout_message: { required, minLength: minLength(20), maxLength: maxLength(1024) },
    help_keywords: { maxLength: maxLength(255) },
    help_message: { required, minLength: minLength(20), maxLength: maxLength(1024) },
    privacy_policy_link: { url, maxLength: maxLength(2048) },
    terms_and_conditions_link: { maxLength: maxLength(2048) },
    embedded_link_sample: { url, maxLength: maxLength(255) },
}));

const validate = useVuelidate(rules, form, { $externalResults: toRef(props, 'externalResults') });
</script>
